/**
 * Запросы складского раздела — одинаковыми телами, чтобы кеш финансового
 * блока отдавал один ответ всем, кто его спрашивает (панель, разделы, детализация).
 */

import { num, useReport, type ReportState } from '../../finance/data'
import { todayIso, type DateRange } from '../../finance/period'

/** Виды складов. Без фильтра сервер отдаёт только товарные. */
export const WAREHOUSE_KINDS = { goods: 'Товарный', transit: 'Товары в пути', assets: 'Основные средства', other: 'Прочее нетоварное' } as const

/**
 * Строка витрины — пара «склад × позиция», поэтому дни без движения в ней точные.
 * Вид склада и источник даты движения от позиции не дробят строку: у пары
 * «склад × позиция» они одни. Запрашиваются все виды складов, а делятся
 * на экране: запас — товарные, товары в пути и нетоварные — отдельной строкой.
 */
export const STOCK_BODY = {
  group_by: ['snapshot_date', 'warehouse', 'nomenclature', 'warehouse_kind', 'move_date_source'],
  metrics: ['amount', 'quantity', 'reserved_qty', 'days_without_move'],
  filters: { warehouse_kind: Object.values(WAREHOUSE_KINDS) },
  order_by: { key: 'amount', direction: 'desc' as const },
}

/** Позиция не двигалась за всю загруженную историю: дни — нижняя граница. */
export const NO_MOVES = 'Движений в загруженной истории нет'
/** 1С прислала свою дату последнего движения. */
export const FROM_1C = 'Дата из 1С'

export function useStockCurrent(enabled = true): ReportState {
  return useReport('stock_current', enabled ? STOCK_BODY : null)
}

export function useStockSnapshotDate(enabled: boolean): string | null {
  const state = useStockCurrent(enabled)
  const value = state.data?.rows[0]?.snapshot_date
  return value ? String(value).slice(0, 10) : null
}

const iso = (year: number, month: number, day = 1) => new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10)

/** 24 месяца до конца текущего: из них видно, где есть снимки, а где продажи. */
export function coverageRange(): DateRange {
  const today = todayIso()
  const year = Number(today.slice(0, 4))
  const month = Number(today.slice(5, 7)) - 1
  return { from: iso(year, month - 23), to: iso(year, month + 1, 0) }
}

export function useStockMonths(enabled = true): ReportState {
  return useReport(
    'stock_turnover',
    enabled ? { period: coverageRange(), group_by: ['period'], metrics: ['revenue', 'cogs', 'avg_stock_value'] } : null,
  )
}

export interface MonthCoverage {
  month: string
  revenue: number
  cogs: number
  stock: number
}

export function coverageOf(state: ReportState): MonthCoverage[] {
  return [...(state.data?.rows ?? [])]
    .map((row) => ({ month: String(row.period).slice(0, 10), revenue: num(row.revenue), cogs: num(row.cogs), stock: num(row.avg_stock_value) }))
    .sort((a, b) => a.month.localeCompare(b.month))
}

/** Последний месяц, где есть продажи. */
export function lastSalesMonth(months: MonthCoverage[]): string | null {
  const list = months.filter((item) => item.revenue > 0)
  return list[list.length - 1]?.month ?? null
}

/** Последний месяц, где есть и снимок остатков, и себестоимость продаж. */
export function lastTurnoverMonth(months: MonthCoverage[]): string | null {
  const list = months.filter((item) => item.stock > 0 && item.cogs > 0)
  return list[list.length - 1]?.month ?? null
}

export function monthEnd(month: string): string {
  return iso(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)
}

export function shiftMonth(month: string, steps: number): string {
  return iso(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + steps)
}

export function daysIn(month: string): number {
  return Number(monthEnd(month).slice(8, 10))
}

/** Возраст запаса по дням без движения. Порядковая шкала одного оттенка: темнее — старше. */
export const AGES = [
  { key: 'a30', label: 'до 30 дней', test: (days: number) => days <= 30, color: 'var(--age-0)' },
  { key: 'a90', label: '31–90 дней', test: (days: number) => days > 30 && days <= 90, color: 'var(--age-1)' },
  { key: 'a180', label: '91–180 дней', test: (days: number) => days > 90 && days <= 180, color: 'var(--age-2)' },
  { key: 'a365', label: '181–365 дней', test: (days: number) => days > 180 && days <= 365, color: 'var(--age-3)' },
  { key: 'aold', label: 'свыше года', test: (days: number) => days > 365, color: 'var(--age-4)' },
] as const

export const NO_DATE = { key: 'nodate', label: 'дата движения неизвестна', color: 'var(--stock-nodate)' }

export function ageKey(days: unknown): string {
  if (days === null || days === undefined || days === '') return NO_DATE.key
  const value = Number(days)
  return AGES.find((age) => age.test(value))?.key ?? NO_DATE.key
}
